/**
 * Utility functions to manage liked albums in localStorage
 * This tracks which albums the current browser has liked
 */

const LIKED_ALBUMS_KEY = 'likedAlbums';

/**
 * Get all liked album IDs from localStorage
 */
export const getLikedAlbums = (): Set<number> => {
    try {
        const stored = localStorage.getItem(LIKED_ALBUMS_KEY);
        if (!stored) return new Set<number>();
        const ids = JSON.parse(stored) as number[];
        return new Set(ids);
    } catch (error) {
        console.error('Error reading liked albums from localStorage:', error);
        return new Set<number>();
    }
};

/**
 * Check if an album is liked
 */
export const isAlbumLiked = (albumId: number): boolean => {
    const likedAlbums = getLikedAlbums();
    return likedAlbums.has(albumId);
};

/**
 * Add an album to liked albums
 */
export const addLikedAlbum = (albumId: number): void => {
    try {
        const likedAlbums = getLikedAlbums();
        likedAlbums.add(albumId);
        localStorage.setItem(LIKED_ALBUMS_KEY, JSON.stringify(Array.from(likedAlbums)));
    } catch (error) {
        console.error('Error saving liked album to localStorage:', error);
    }
};

/**
 * Remove an album from liked albums
 */
export const removeLikedAlbum = (albumId: number): void => {
    try {
        const likedAlbums = getLikedAlbums();
        likedAlbums.delete(albumId);
        localStorage.setItem(LIKED_ALBUMS_KEY, JSON.stringify(Array.from(likedAlbums)));
    } catch (error) {
        console.error('Error removing liked album from localStorage:', error);
    }
};

/**
 * Toggle an album's liked status
 * Returns true if album is now liked, false if unliked
 */
export const toggleLikedAlbum = (albumId: number): boolean => {
    if (isAlbumLiked(albumId)) {
        removeLikedAlbum(albumId);
        return false;
    } else {
        addLikedAlbum(albumId);
        return true;
    }
};

